import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, ChevronLeft, Info, Check, AlertCircle,
         Store, Handshake, SprayCan, GraduationCap, Users, ClipboardCheck,
         ChefHat, BookOpen, UserCog, Wrench, UserPlus } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import SignaturePad from "@/components/SignaturePad";
import RecrutementRequestForm from "@/components/rh/RecrutementRequestForm";
import SupportEscalationSection, { emptyEscalation, createEscalationTicket } from "@/components/support/SupportEscalationSection";
import {
  ACTIVITY_TYPES, RDV_INTERLOCUTEURS, RDV_OBJETS, TECH_TYPES,
  ENTRETIEN_TYPES, ENTRETIEN_LIEUX, MAINTENANCE_ACTIONS,
  AUDIT_TEMPLATE_CORNER, AUDIT_TEMPLATE_HYGIENE,
  SATISFACTION_OPTS, RESULTAT_OPTS,
  HYGIENE_DOCS, HYGIENE_LANGS,
} from "@/lib/activityTypes";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import DynamicFormRenderer, { missingRequired, computeScore } from "@/components/forms/DynamicFormRenderer";
import { FileText } from "lucide-react";

const ICONS = { Store, Handshake, SprayCan, GraduationCap, Users, ClipboardCheck, ChefHat, BookOpen, UserCog, Wrench, FileText };
const FORM_MODULES = "Centre des opérations,Interventions";
const RECRUT_TYPE = { key: "demande_recrutement", label: "Demande de recrutement", desc: "Besoin de renfort : poste, contrat, heures, délai — plusieurs demandes dans une même fiche.", recrutement: true };

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="text-xs font-semibold tracking-wider text-slate-500 uppercase block mb-1">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
    {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
  </div>
);

const Input = (p) => <input {...p} className={`w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 ${p.className || ""}`}/>;
const Textarea = (p) => <textarea {...p} className={`w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-none ${p.className || ""}`}/>;
const _optsFromChildren = (children) => {
  const out = []; let placeholder;
  const walk = (c) => {
    if (c == null || c === false) return;
    if (Array.isArray(c)) return c.forEach(walk);
    if (c.type === "option") {
      const label = Array.isArray(c.props.children) ? c.props.children.join("") : String(c.props.children ?? "");
      const value = c.props.value ?? label;
      if (value === "") placeholder = label; else out.push({ value, label });
    } else if (c.props?.children) walk(c.props.children);
  };
  walk(children);
  return { options: out, placeholder };
};
const Select = ({ value, onChange, children, className = "", required, ...rest }) => {
  const { options, placeholder } = _optsFromChildren(children);
  return <SearchSelect value={value ?? ""} onChange={v => onChange({ target: { value: v } })} options={options} placeholder={placeholder || "—"}
                       size="xl" menuWidth={280} className={`w-full ${className}`} testid={rest["data-testid"]}/>;
};

function ChipPicker({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map(o => {
        const active = value === o.v;
        const color = o.color || "blue";
        return (
          <button key={o.v} type="button" onClick={() => onChange(o.v)}
                  data-testid={`${testid}-${o.v}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    active ? `bg-${color}-500 text-white border-${color}-500`
                           : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Type picker (step 0) ─────────────────────────────────────────────
function TypePicker({ userRole, onPick, onClose, customForms }) {
  const allowed = ACTIVITY_TYPES.filter(t => !t.roles || t.roles.includes(userRole));
  return (
    <div>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <h2 className="font-display text-xl font-semibold">Nouvelle activité terrain</h2>
        <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
      </div>
      <div className="p-6">
        <p className="text-sm text-slate-500 mb-4">Choisissez le type d'activité — chaque formulaire est optimisé pour se remplir en 5 min.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[260px] overflow-y-auto scrollbar-thin pr-1" data-testid="activity-type-list">
          {allowed.map(t => {
            const Icon = ICONS[t.icon] || Store;
            return (
              <button key={t.key} type="button" onClick={() => onPick(t)}
                      data-testid={`activity-type-${t.key}`}
                      className="text-left rounded-xl border border-slate-200 hover:border-blue-400 hover:shadow-md hover:shadow-blue-100 bg-white p-4 flex gap-3 transition group">
                <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${t.color}18`, color: t.color }}>
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 text-sm">{t.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-snug">{t.desc}</div>
                </div>
              </button>
            );
          })}
          <button type="button" onClick={() => onPick(RECRUT_TYPE)} data-testid="activity-type-demande_recrutement"
                  className="text-left rounded-xl border border-purple-200 hover:border-purple-400 hover:shadow-md hover:shadow-purple-100 bg-purple-50/40 p-4 flex gap-3 transition group">
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-purple-100 text-purple-600"><UserPlus className="w-5 h-5"/></div>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 text-sm">{RECRUT_TYPE.label}</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-snug">{RECRUT_TYPE.desc}</div>
            </div>
          </button>
          {(customForms || []).map(f => {
                const color = f.settings?.color || "#0b1f4d";
                return (
                  <button key={f.id} type="button" onClick={() => onPick({ key: `form_${f.id}`, label: f.name, desc: f.description || f.category, color, icon: "FileText", visit_type: f.name, custom: true, form: f })}
                          data-testid={`activity-type-form-${f.id}`}
                          className="text-left rounded-xl border border-slate-200 hover:border-blue-400 hover:shadow-md hover:shadow-blue-100 bg-white p-4 flex gap-3 transition group">
                    <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}18`, color }}><FileText className="w-5 h-5"/></div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 text-sm">{f.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 leading-snug">{f.description || f.category}{f.settings?.estimated_minutes ? ` · ~${f.settings.estimated_minutes} min` : ""}</div>
                    </div>
                  </button>
                );
              })}
        </div>
      </div>
    </div>
  );
}

// ─── Shared header (store + date) ────────────────────────────────────
function CommonHeader({ form, setForm, stores, sectors, showStore = true }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Field label="Date" required>
        <Input type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="af-date"/>
      </Field>
      <Field label="Durée (min)">
        <Input type="number" min="5" value={form.duration_min} onChange={e => setForm({...form, duration_min: e.target.value})} data-testid="af-duration"/>
      </Field>
      <Field label={showStore ? "Magasin / Corner" : "Magasin (optionnel)"} required={showStore}>
        <Select value={form.store_id} required={showStore}
                onChange={e => {
                  const st = stores.find(s => s.id === e.target.value);
                  setForm({...form, store_id: e.target.value, sector_id: st?.sector_id || form.sector_id});
                }} data-testid="af-store">
          <option value="">— Choisir —</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}{s.enseigne ? ` · ${s.enseigne}` : ""}</option>)}
        </Select>
      </Field>
    </div>
  );
}

// ─── Individual form components (10 types) ───────────────────────────
// each returns partial payload; parent stores in form.payload

function VisiteStandard({ p, setP }) {
  return (
    <>
      <Field label="Objet de la visite" required>
        <Input value={p.objet || ""} onChange={e => setP({...p, objet: e.target.value})} placeholder="Ex : contrôle vitrine, focus DLC…" data-testid="af-objet"/>
      </Field>
      <Field label="Commentaire">
        <Textarea rows={4} value={p.commentaire || ""} onChange={e => setP({...p, commentaire: e.target.value})} data-testid="af-commentaire"/>
      </Field>
      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={!!p.info_important} onChange={e => setP({...p, info_important: e.target.checked})} data-testid="af-info-important"/>
        <Info className="w-4 h-4 text-amber-500"/>
        Information importante (fait remonter la visite en haut du fil)
      </label>
    </>
  );
}

function RdvMagasin({ p, setP }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Interlocuteur" required>
          <Select value={p.interlocuteur || ""} required onChange={e => setP({...p, interlocuteur: e.target.value})} data-testid="af-interloc">
            <option value="">—</option>
            {RDV_INTERLOCUTEURS.map(x => <option key={x}>{x}</option>)}
          </Select>
        </Field>
        <Field label="Objet" required>
          <Select value={p.objet || ""} required onChange={e => setP({...p, objet: e.target.value})} data-testid="af-objet">
            <option value="">—</option>
            {RDV_OBJETS.map(x => <option key={x}>{x}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="Déroulé du rendez-vous" required>
        <Textarea rows={5} required value={p.compte_rendu || ""} onChange={e => setP({...p, compte_rendu: e.target.value})} data-testid="af-cr"/>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={!!p.cr_envoye_magasin} onChange={e => setP({...p, cr_envoye_magasin: e.target.checked})} data-testid="af-cr-magasin"/>
          CR envoyé au magasin
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={!!p.cr_envoye_n1} onChange={e => setP({...p, cr_envoye_n1: e.target.checked})} data-testid="af-cr-n1"/>
          CR envoyé au N+1
        </label>
      </div>
      <Field label="État du RDV">
        <ChipPicker value={p.satisfaction} onChange={v => setP({...p, satisfaction: v})} options={SATISFACTION_OPTS} testid="af-satisfaction"/>
      </Field>
    </>
  );
}

function FormationHygiene({ p, setP }) {
  const lang = p.lang || "fr";
  const formationUrl = HYGIENE_DOCS.formation[lang];
  const questionnaireUrl = HYGIENE_DOCS.questionnaire[lang];
  return (
    <>
      <Field label="Langue des documents de formation" required>
        <div className="flex flex-wrap gap-2" data-testid="af-lang">
          {HYGIENE_LANGS.map(l => {
            const active = lang === l.v;
            const available = !!HYGIENE_DOCS.formation[l.v];
            return (
              <button key={l.v} type="button" onClick={() => setP({...p, lang: l.v})}
                      data-testid={`af-lang-${l.v}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-1.5 transition ${
                        active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                      } ${!available && !active ? "opacity-60" : ""}`}>
                <span>{l.flag}</span> {l.label}
                {!available && <span className="text-[9px] font-normal">(à venir)</span>}
              </button>
            );
          })}
        </div>
      </Field>
      <div className="rounded-xl bg-cyan-50 border border-cyan-100 p-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-cyan-800">📎 Supports :</span>
        {formationUrl ? (
          <a href={formationUrl} target="_blank" rel="noreferrer" data-testid="af-doc-formation"
             className="text-xs font-semibold text-blue-700 hover:text-blue-900 underline">Formation hygiène (PDF)</a>
        ) : <span className="text-xs text-slate-500 italic">Formation ({lang}) : à venir</span>}
        <span className="text-slate-300">·</span>
        {questionnaireUrl ? (
          <a href={questionnaireUrl} target="_blank" rel="noreferrer" data-testid="af-doc-quest"
             className="text-xs font-semibold text-blue-700 hover:text-blue-900 underline">Questionnaire fin de formation (PDF)</a>
        ) : <span className="text-xs text-slate-500 italic">Questionnaire ({lang}) : à venir</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Nom et prénom du collaborateur" required>
          <Input value={p.nom_prenom || ""} required onChange={e => setP({...p, nom_prenom: e.target.value})} data-testid="af-nom"/>
        </Field>
        <Field label="Note obtenue (/20)">
          <Input type="number" min={0} max={20} value={p.note ?? ""} onChange={e => setP({...p, note: e.target.value ? Number(e.target.value) : null})} data-testid="af-note"/>
        </Field>
      </div>
      <Field label="Format de la formation" required>
        <ChipPicker value={p.format} onChange={v => setP({...p, format: v})}
                    options={[{v:"en_ligne",label:"En ligne"},{v:"papier",label:"Papier"}]} testid="af-format"/>
        {p.format === "papier" && (
          <div className="mt-2 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
            <AlertCircle className="w-4 h-4 shrink-0"/>
            Ajoutez une photo du questionnaire signé (obligatoire).
          </div>
        )}
      </Field>
      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={!!p.tenue_remise} onChange={e => setP({...p, tenue_remise: e.target.checked})} data-testid="af-tenue"/>
        Tenue de travail remise
      </label>
      <Field label="Commentaire">
        <Textarea rows={3} value={p.commentaire || ""} onChange={e => setP({...p, commentaire: e.target.value})} data-testid="af-commentaire"/>
      </Field>
      <SignaturePad value={p.signature} onChange={v => setP({...p, signature: v})} label="Signature du collaborateur" testid="af-signature"/>
    </>
  );
}

function FormationHygieneRt({ p, setP }) {
  return (
    <>
      <Field label="Objet de la formation" required>
        <Input value={p.objet || ""} required onChange={e => setP({...p, objet: e.target.value})} placeholder="Ex : rappel HACCP, formation nouveau collab…" data-testid="af-objet"/>
      </Field>
      <Field label="Détail" required>
        <Textarea rows={4} required value={p.detail || ""} onChange={e => setP({...p, detail: e.target.value})} data-testid="af-detail"/>
      </Field>
      <Field label="Signalement particulier">
        <Textarea rows={2} value={p.signalement || ""} onChange={e => setP({...p, signalement: e.target.value})} data-testid="af-signalement"/>
      </Field>
      <Field label="Résultat" required>
        <ChipPicker value={p.resultat} onChange={v => setP({...p, resultat: v})} options={RESULTAT_OPTS} testid="af-resultat"/>
      </Field>
      <Field label="Nom et prénom">
        <Input value={p.nom_prenom || ""} onChange={e => setP({...p, nom_prenom: e.target.value})} data-testid="af-nom"/>
      </Field>
      <SignaturePad value={p.signature} onChange={v => setP({...p, signature: v})} label="Signature" testid="af-signature"/>
    </>
  );
}

function PointEquipe({ p, setP }) {
  const participants = p.participants || [];
  const addP = () => setP({...p, participants: [...participants, { nom: "", signature: null }]});
  const setPart = (i, k, v) => { const arr = [...participants]; arr[i] = {...arr[i], [k]: v}; setP({...p, participants: arr}); };
  const del = (i) => setP({...p, participants: participants.filter((_, j) => j !== i)});
  return (
    <>
      <Field label="Objet du point" required>
        <Input value={p.objet || ""} required onChange={e => setP({...p, objet: e.target.value})} data-testid="af-objet"/>
      </Field>
      <Field label="Déroulé" required>
        <Textarea rows={4} required value={p.deroule || ""} onChange={e => setP({...p, deroule: e.target.value})} data-testid="af-deroule"/>
      </Field>
      <Field label="Prochaine action">
        <Textarea rows={2} value={p.prochaine_action || ""} onChange={e => setP({...p, prochaine_action: e.target.value})} data-testid="af-next-action"/>
      </Field>
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Participants</span>
          <button type="button" onClick={addP} data-testid="af-add-part" className="text-xs font-semibold text-blue-600 hover:text-blue-700">+ Ajouter</button>
        </div>
        <div className="space-y-3">
          {participants.map((pp, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Input value={pp.nom} placeholder="Nom prénom" onChange={e => setPart(i, "nom", e.target.value)} data-testid={`af-part-nom-${i}`}/>
                <button type="button" onClick={() => del(i)} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600"><X className="w-4 h-4"/></button>
              </div>
              <SignaturePad value={pp.signature} onChange={v => setPart(i, "signature", v)} label="Signature" testid={`af-part-sig-${i}`}/>
            </div>
          ))}
          {participants.length === 0 && <div className="text-xs text-slate-400 italic">Aucun participant ajouté (optionnel).</div>}
        </div>
      </div>
    </>
  );
}

function AuditCorner({ p, setP }) {
  const template = p.sub_type === "hygiene" ? AUDIT_TEMPLATE_HYGIENE : AUDIT_TEMPLATE_CORNER;
  const answers = p.answers || {};
  const setAns = (label, val) => setP({...p, answers: {...answers, [label]: val}});
  const setCat = (label, cmt) => setP({...p, comments: {...(p.comments||{}), [label]: cmt}});

  // Compute score
  const allItems = template.flatMap(c => c.items);
  const applicable = allItems.filter(l => answers[l] && answers[l] !== "na");
  const ok = applicable.filter(l => answers[l] === "conforme").length;
  const pct = applicable.length ? Math.round(ok * 100 / applicable.length) : 0;

  useEffect(() => {
    setP(prev => ({...prev, score_pct: pct, items_ok: ok, items_total: applicable.length}));
    // eslint-disable-next-line
  }, [pct, ok, applicable.length]);

  return (
    <>
      <Field label="Type d'audit" required>
        <ChipPicker value={p.sub_type || "corner"} onChange={v => setP({...p, sub_type: v, answers: {}, comments: {}})}
                    options={[{v:"corner",label:"Audit corner"},{v:"hygiene",label:"Audit hygiène"}]} testid="af-sub-type"/>
      </Field>
      <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-100 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold tracking-wider text-emerald-700 uppercase">Taux de conformité</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-1">{pct}%</div>
            <div className="text-xs text-slate-500">{ok} / {applicable.length} items conformes ({allItems.length - applicable.length} N/A)</div>
          </div>
          <div className={`w-16 h-16 rounded-full grid place-items-center text-white font-bold ${pct >= 85 ? "bg-emerald-500" : pct >= 65 ? "bg-amber-500" : "bg-rose-500"}`}>
            {pct}%
          </div>
        </div>
      </div>
      {template.map((cat, ci) => (
        <div key={ci} className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 font-semibold text-sm text-slate-700">{cat.cat}</div>
          <div className="divide-y divide-slate-100">
            {cat.items.map((item, ii) => {
              const val = answers[item];
              return (
                <div key={ii} className="px-4 py-2.5 flex items-center gap-2">
                  <div className="flex-1 text-sm text-slate-700">{item}</div>
                  {["conforme", "non_conforme", "na"].map(v => (
                    <button key={v} type="button" onClick={() => setAns(item, v)}
                            data-testid={`af-audit-${ci}-${ii}-${v}`}
                            className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition ${
                              val === v ? (v === "conforme" ? "bg-emerald-500 text-white border-emerald-500"
                                          : v === "non_conforme" ? "bg-rose-500 text-white border-rose-500"
                                          : "bg-slate-400 text-white border-slate-400")
                                        : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                            }`}>
                      {v === "conforme" ? "✓" : v === "non_conforme" ? "✕" : "N/A"}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <Field label="Commentaire général">
        <Textarea rows={3} value={p.commentaire || ""} onChange={e => setP({...p, commentaire: e.target.value})} data-testid="af-commentaire"/>
      </Field>
    </>
  );
}

function FormationTechnique({ p, setP }) {
  const [acquisNew, setAcquisNew] = useState("");
  const [nonNew, setNonNew] = useState("");
  const acquis = p.acquis || []; const non_acquis = p.non_acquis || [];
  return (
    <>
      <Field label="Type de formation" required>
        <Select value={p.formation_type || ""} required onChange={e => setP({...p, formation_type: e.target.value})} data-testid="af-type-form">
          <option value="">—</option>
          {TECH_TYPES.map(x => <option key={x}>{x}</option>)}
        </Select>
      </Field>
      <Field label="Détail de la formation" required>
        <Textarea rows={4} required value={p.detail || ""} onChange={e => setP({...p, detail: e.target.value})} data-testid="af-detail"/>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Éléments acquis">
          <div className="flex gap-1">
            <Input value={acquisNew} onChange={e => setAcquisNew(e.target.value)} placeholder="Ex : Découpe saumon"/>
            <button type="button" onClick={() => { if (acquisNew.trim()) { setP({...p, acquis: [...acquis, acquisNew.trim()]}); setAcquisNew(""); } }}
                    className="px-3 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600" data-testid="af-add-acquis">+</button>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {acquis.map((a, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center gap-1">
                <Check className="w-3 h-3"/> {a}
                <button type="button" onClick={() => setP({...p, acquis: acquis.filter((_, j) => j !== i)})} className="ml-1 text-emerald-600 hover:text-emerald-900">×</button>
              </span>
            ))}
          </div>
        </Field>
        <Field label="Éléments non acquis">
          <div className="flex gap-1">
            <Input value={nonNew} onChange={e => setNonNew(e.target.value)} placeholder="Ex : Nigiri façonnage"/>
            <button type="button" onClick={() => { if (nonNew.trim()) { setP({...p, non_acquis: [...non_acquis, nonNew.trim()]}); setNonNew(""); } }}
                    className="px-3 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600" data-testid="af-add-non-acquis">+</button>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {non_acquis.map((a, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-100 flex items-center gap-1">
                {a}
                <button type="button" onClick={() => setP({...p, non_acquis: non_acquis.filter((_, j) => j !== i)})} className="ml-1 text-rose-600 hover:text-rose-900">×</button>
              </span>
            ))}
          </div>
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Note (/10)">
          <Input type="number" min={0} max={10} value={p.note ?? ""} onChange={e => setP({...p, note: e.target.value ? Number(e.target.value) : null})} data-testid="af-note"/>
        </Field>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pb-2">
            <input type="checkbox" checked={!!p.envoi_n1} onChange={e => setP({...p, envoi_n1: e.target.checked})} data-testid="af-envoi-n1"/>
            Envoyer au N+1 (Responsable secteur + Directeur)
          </label>
        </div>
      </div>
    </>
  );
}

function FormationMethode({ p, setP }) {
  const participants = p.participants || [];
  const addP = () => setP({...p, participants: [...participants, { nom: "", signature: null }]});
  const setPart = (i, k, v) => { const arr = [...participants]; arr[i] = {...arr[i], [k]: v}; setP({...p, participants: arr}); };
  const del = (i) => setP({...p, participants: participants.filter((_, j) => j !== i)});
  return (
    <>
      <Field label="Méthode ou process traité" required>
        <Input value={p.methode || ""} required onChange={e => setP({...p, methode: e.target.value})} placeholder="Ex : Rappel process commande fournisseur" data-testid="af-methode"/>
      </Field>
      <Field label="Déroulé" required>
        <Textarea rows={4} required value={p.deroule || ""} onChange={e => setP({...p, deroule: e.target.value})} data-testid="af-deroule"/>
      </Field>
      <Field label="Résultat">
        <ChipPicker value={p.resultat} onChange={v => setP({...p, resultat: v})} options={RESULTAT_OPTS} testid="af-resultat"/>
      </Field>
      <Field label="Prochaine action">
        <Textarea rows={2} value={p.prochaine_action || ""} onChange={e => setP({...p, prochaine_action: e.target.value})} data-testid="af-next-action"/>
      </Field>
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Salariés formés</span>
          <button type="button" onClick={addP} data-testid="af-add-part" className="text-xs font-semibold text-blue-600 hover:text-blue-700">+ Ajouter</button>
        </div>
        <div className="space-y-3">
          {participants.map((pp, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Input value={pp.nom} placeholder="Nom prénom" onChange={e => setPart(i, "nom", e.target.value)}/>
                <button type="button" onClick={() => del(i)} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600"><X className="w-4 h-4"/></button>
              </div>
              <SignaturePad value={pp.signature} onChange={v => setPart(i, "signature", v)} label="Signature" testid={`af-mth-sig-${i}`}/>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function EntretienRh({ p, setP }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Type d'entretien" required>
          <Select value={p.type_entretien || ""} required onChange={e => setP({...p, type_entretien: e.target.value})} data-testid="af-type-ent">
            <option value="">—</option>
            {ENTRETIEN_TYPES.map(x => <option key={x}>{x}</option>)}
          </Select>
        </Field>
        <Field label="Lieu" required>
          <Select value={p.lieu || ""} required onChange={e => setP({...p, lieu: e.target.value})} data-testid="af-lieu">
            <option value="">—</option>
            {ENTRETIEN_LIEUX.map(x => <option key={x}>{x}</option>)}
          </Select>
        </Field>
      </div>
      {p.lieu === "Autre" && (
        <Field label="Préciser le lieu"><Input value={p.lieu_autre || ""} onChange={e => setP({...p, lieu_autre: e.target.value})}/></Field>
      )}
      <Field label="Conditions" required>
        <ChipPicker value={p.condition} onChange={v => setP({...p, condition: v})}
                    options={[{v:"favorable",label:"Favorables",color:"emerald"},{v:"defavorable",label:"Défavorables",color:"rose"}]} testid="af-condition"/>
        {p.condition === "defavorable" && (
          <Input className="mt-2" value={p.condition_precision || ""} onChange={e => setP({...p, condition_precision: e.target.value})} placeholder="Préciser…"/>
        )}
      </Field>
      <Field label="Déroulé et éléments abordés" required>
        <Textarea rows={4} required value={p.deroule || ""} onChange={e => setP({...p, deroule: e.target.value})} data-testid="af-deroule"/>
      </Field>
      <Field label="Avis et éléments du salarié">
        <Textarea rows={3} value={p.avis_salarie || ""} onChange={e => setP({...p, avis_salarie: e.target.value})} data-testid="af-avis"/>
      </Field>
      <Field label="Nom et prénom" required>
        <Input value={p.nom_prenom || ""} required onChange={e => setP({...p, nom_prenom: e.target.value})} data-testid="af-nom"/>
      </Field>
      <SignaturePad value={p.signature} onChange={v => setP({...p, signature: v})} label="Signature du salarié" testid="af-signature"/>
    </>
  );
}

function Maintenance({ p, setP }) {
  return (
    <>
      <Field label="Action réalisée" required>
        <Select value={p.action || ""} required onChange={e => setP({...p, action: e.target.value})} data-testid="af-action">
          <option value="">—</option>
          {MAINTENANCE_ACTIONS.map(x => <option key={x}>{x}</option>)}
        </Select>
      </Field>
      {(p.action === "Petite réparation" || p.action === "Autre") && (
        <Field label="Préciser"><Input value={p.action_autre || ""} onChange={e => setP({...p, action_autre: e.target.value})}/></Field>
      )}
      <Field label="Détail de l'intervention" required>
        <Textarea rows={4} required value={p.intervention || ""} onChange={e => setP({...p, intervention: e.target.value})} data-testid="af-intervention"/>
      </Field>
      <Field label="Résolu ?" required>
        <ChipPicker value={p.resolu === true ? "oui" : p.resolu === false ? "non" : ""}
                    onChange={v => setP({...p, resolu: v === "oui"})}
                    options={[{v:"oui",label:"Oui",color:"emerald"},{v:"non",label:"Non",color:"rose"}]} testid="af-resolu"/>
      </Field>
      <Field label="Besoin après intervention">
        <Textarea rows={2} value={p.besoin_apres || ""} onChange={e => setP({...p, besoin_apres: e.target.value})} data-testid="af-besoin"/>
      </Field>
      <Field label="Prochaine action">
        <Input value={p.prochaine_action || ""} onChange={e => setP({...p, prochaine_action: e.target.value})} data-testid="af-next-action"/>
      </Field>
    </>
  );
}

const FORM_COMPONENTS = {
  visite_standard: VisiteStandard, rdv_magasin: RdvMagasin,
  formation_hygiene: FormationHygiene, formation_hygiene_rt: FormationHygieneRt,
  point_equipe: PointEquipe, audit_corner: AuditCorner,
  formation_technique: FormationTechnique, formation_methode: FormationMethode,
  entretien_rh: EntretienRh, maintenance: Maintenance,
};

// ─── Main modal ──────────────────────────────────────────────────────
export default function ActivityFormModal({ open, onClose, onSaved, stores, sectors, userRole }) {
  const [type, setType] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, duration_min: 60, store_id: "", sector_id: "", photos: [], next_visit_date: "" });
  const [payload, setPayload] = useState({});
  const [esc, setEsc] = useState(emptyEscalation());
  const [saving, setSaving] = useState(false);
  const [customForms, setCustomForms] = useState([]);

  useEffect(() => {
    if (open) api.get("/forms/available", { params: { module: FORM_MODULES } }).then(r => setCustomForms(r.data || [])).catch(() => setCustomForms([]));
    if (!open) { setType(null); setForm({ date: today, duration_min: 60, store_id: "", sector_id: "", photos: [], next_visit_date: "" }); setPayload({}); setEsc(emptyEscalation()); }
    // eslint-disable-next-line
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    // Formation hygiène papier → photo obligatoire
    if (type.key === "formation_hygiene" && payload.format === "papier" && (!form.photos || form.photos.length === 0)) {
      toast.error("Ajoutez une photo du questionnaire signé (formation papier)."); return;
    }
    if (type.custom) {
      const miss = missingRequired(type.form, payload.values || {});
      if (miss.length) { toast.error(`Champs obligatoires : ${miss.slice(0, 3).join(", ")}${miss.length > 3 ? "…" : ""}`); return; }
    }
    setSaving(true);
    try {
      if (type.custom) {
        const score = type.form.settings?.scoring?.enabled ? computeScore(type.form, payload.values || {}) : null;
        const firstText = Object.values(payload.values || {}).find(v => typeof v === "string" && v.length > 3);
        const res = await api.post("/activities", {
          date: form.date, store_id: form.store_id || null, sector_id: form.sector_id || null,
          visit_type: type.form.name, type_key: type.key, description: String(firstText || type.form.name).slice(0, 500),
          duration_min: Number(form.duration_min || type.form.settings?.estimated_minutes || 30), photos: form.photos || [], status: "completed",
          payload: { form_id: type.form.id, form_name: type.form.name, values: payload.values || {}, score }, next_visit_date: form.next_visit_date || null,
        });
        await api.post(`/forms/${type.form.id}/submissions`, { module: "Centre des opérations", store_id: form.store_id || null, date: form.date, values: payload.values || {}, score, photos: form.photos || [], activity_id: res.data?.id });
        toast.success(`« ${type.form.name} » enregistré`);
        onSaved?.(); onClose?.(); return;
      }
      const description =
        payload.compte_rendu || payload.detail || payload.deroule ||
        payload.objet || payload.intervention || payload.methode ||
        `${type.label}${payload.formation_type ? ` — ${payload.formation_type}` : ""}${payload.action ? ` — ${payload.action}` : ""}`;
      const res = await api.post("/activities", {
        date: form.date,
        store_id: form.store_id || null,
        sector_id: form.sector_id || null,
        visit_type: type.visit_type,
        type_key: type.key,
        description: description.slice(0, 500),
        duration_min: Number(form.duration_min || 60),
        photos: form.photos || [],
        status: "completed",
        payload,
        next_visit_date: form.next_visit_date || null,
        info_important: !!payload.info_important,
      });
      toast.success("Activité enregistrée");
      if (esc.enabled) {
        if (!esc.support_service) toast.error("Escalade non envoyée : aucun service support choisi");
        else {
          const storeName = (stores || []).find(s => s.id === form.store_id)?.name;
          const t = await createEscalationTicket(esc, { source_type: "activite", source_id: res.data?.id, titre: `${type.label}${storeName ? ` · ${storeName}` : ""}`, store_id: form.store_id || null });
          if (t) toast.success(`Ticket support ${esc.support_service} créé`); else toast.error("Le ticket support n'a pas pu être créé");
        }
      }
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const FormBody = type ? (type.custom ? (({ p, setP }) => <DynamicFormRenderer form={type.form} values={p.values || {}} onChange={values => setP({ ...p, values })} stores={stores}/>) : FORM_COMPONENTS[type.key]) : null;
  const IconC = type ? (ICONS[type.icon] || Store) : null;

  return (
    <div className="fixed inset-0 z-[1200] bg-slate-900/50 backdrop-blur-sm grid place-items-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-2xl w-full max-w-2xl border border-slate-200 shadow-xl my-4 max-h-[95vh] flex flex-col">
        {!type && <TypePicker userRole={userRole} onPick={t => setType(t)} onClose={onClose} customForms={customForms}/>}
        {type?.recrutement && <RecrutementRequestForm inline source="cockpit" onBack={() => setType(null)} onClose={onClose} onSaved={() => { onSaved?.(); onClose?.(); }}/>}
        {type && !type.recrutement && (
          <form onSubmit={submit} className="flex flex-col min-h-0">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
              <button type="button" onClick={() => setType(null)} data-testid="af-back"
                      className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><ChevronLeft className="w-4 h-4"/></button>
              <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: `${type.color}18`, color: type.color }}>
                <IconC className="w-4 h-4"/>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-lg font-semibold truncate">{type.label}</h2>
                <div className="text-xs text-slate-500 truncate">{type.desc}</div>
              </div>
              <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto min-h-0 flex-1">
              <CommonHeader form={form} setForm={setForm} stores={stores} sectors={sectors}
                            showStore={type.key !== "entretien_rh" && !type.custom}/>
              <FormBody p={payload} setP={setPayload}/>
              <Field label="Photos / Pièces jointes">
                <PhotoUploader value={form.photos} onChange={photos => setForm({...form, photos})} testid="af-photos"/>
              </Field>
              <Field label="Date de prochaine visite" hint="Sera automatiquement ajoutée à votre calendrier.">
                <Input type="date" value={form.next_visit_date || ""} onChange={e => setForm({...form, next_visit_date: e.target.value})} data-testid="af-next-visit"/>
              </Field>
              <SupportEscalationSection value={esc} onChange={setEsc} testid="af-esc"/>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" disabled={saving} data-testid="af-submit"
                      className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 flex items-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Check className="w-4 h-4"/> Enregistrer</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
